import { useState } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import type { ButtonProps } from 'antd';
import QRCode from 'react-qr-code';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload';
import { getLang } from 'shared/store/selectors/lang.selector';
import { buildCheckInUrl } from 'shared/utils/check-in-url';
import { Button } from 'src/components/design-system';
import monitorService from 'src/helpers/monitor.service';
import { downloadBlob } from '../../helpers/download-blob';
import { buildQrFileName, svgToPngBlob } from '../../helpers/qr-png';

const QR_EXPORT_SIZE = 1024;
const QR_EXPORT_MARGIN = 128;

interface DownloadQrButtonProps {
  eventId: string;
  eventTitle?: string;
  size?: ButtonProps['size'];
  type?: ButtonProps['type'];
  className?: string;
}

export function DownloadQrButton({
  eventId,
  eventTitle,
  size,
  type = 'default',
  className,
}: DownloadQrButtonProps) {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      const { renderToStaticMarkup } = await import('react-dom/server');
      const markup = renderToStaticMarkup(
        <QRCode value={buildCheckInUrl({ eventId })} size={QR_EXPORT_SIZE} />,
      );
      const blob = await svgToPngBlob({ markup, size: QR_EXPORT_SIZE, margin: QR_EXPORT_MARGIN });
      downloadBlob(blob, buildQrFileName(eventTitle));
    } catch (error) {
      monitorService.error('Failed to download check-in QR', { error, eventId });
      message.error(lang.DOWNLOAD_QR_FAILED);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      size={size}
      type={type}
      className={className}
      loading={downloading}
      onClick={handleDownload}>
      <FontAwesomeIcon icon={faDownload} />
      {lang.DOWNLOAD_QR}
    </Button>
  );
}
